import {
  createFormHook,
  createFormHookContexts,
  type AppFieldExtendedReactFormApi,
} from '@tanstack/react-form'
import { useReducer } from 'react'
import { ComboboxField } from '~/components/new/app-ui/form-fields/combobox-field'
import { DateField } from '~/components/new/app-ui/form-fields/date-field'
import { EuroCentsField } from '~/components/new/app-ui/form-fields/euro-cents-field'
import { FileField } from '~/components/new/app-ui/form-fields/file-field'
import { MultiComboboxField } from '~/components/new/app-ui/form-fields/multi-combobox-field'
import { NumberField } from '~/components/new/app-ui/form-fields/number-field'
import { PhoneField } from '~/components/new/app-ui/form-fields/phone-field'
import { RadioCardsField } from '~/components/new/app-ui/form-fields/radio-cards-field'
import { SelectField } from '~/components/new/app-ui/form-fields/select-field'
import { SubmitButton } from '~/components/new/app-ui/form-fields/submit-button'
import { SwitchField } from '~/components/new/app-ui/form-fields/switch-field'
import { TextField } from '~/components/new/app-ui/form-fields/text-field'
import { TextFieldGroup } from '~/components/new/app-ui/form-fields/text-field-group'
import { TextareaField } from '~/components/new/app-ui/form-fields/textarea-field'
import { TimeField } from '~/components/new/app-ui/form-fields/time-field'

// Sans composant : importable par les tests `node --test` des formulaires.
export { revalidateLogicWithoutFlicker } from './revalidate-logic'

const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts()

const fieldComponents = {
  TextField,
  SelectField,
  TextFieldGroup,
  NumberField,
  EuroCentsField,
  DateField,
  TimeField,
  SwitchField,
  PhoneField,
  ComboboxField,
  MultiComboboxField,
  FileField,
  TextareaField,
  RadioCardsField,
}

const formComponents = {
  SubmitButton,
}

export const { useAppForm, withFieldGroup, withForm } = createFormHook({
  fieldComponents,
  fieldContext,
  formComponents,
  formContext,
})

export { useFieldContext, useFormContext }

/**
 * Convenience type for sub-components that receive a typed `form` prop.
 * Validator type params are `any` since sub-components don't constrain them.
 */
export type AppFormApi<TFormData> = AppFieldExtendedReactFormApi<
  TFormData,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  typeof fieldComponents,
  typeof formComponents
>

/**
 * Petit gestionnaire d'étapes pour un formulaire multi-étapes : on lui passe la liste ordonnée des
 * noms d'étapes (utiliser `as const` pour conserver le type littéral). `step` est le nom de l'étape
 * courante ; la validation, elle, est gérée à côté (un `form.FormGroup` par étape).
 */
export const useStepper = <TStep extends string>(steps: readonly TStep[]) => {
  const [stepIndex, stepDispatch] = useReducer(
    (prev: number, action: { type: 'next' | 'previous' } | { type: 'set'; step: TStep }) => {
      if (action.type === 'next') return (prev + 1) % steps.length
      if (action.type === 'previous' && prev > 0) return prev - 1
      if (action.type === 'set') return Math.max(0, steps.indexOf(action.step))
      return prev
    },
    0,
  )

  const goNext = () => stepDispatch({ type: 'next' })
  const goBack = () => stepDispatch({ type: 'previous' })
  const setStep = (step: TStep) => stepDispatch({ type: 'set', step })

  return {
    step: steps[stepIndex],
    goNext,
    goBack,
    isFirstStep: stepIndex === 0,
    isLastStep: stepIndex === steps.length - 1,
    setStep,
  }
}
